import { useState } from 'react';
import { isEqual } from 'lodash';

/**
 * shorthand to get local state. If currentState updates (deep equality) then localState is automatically updated
 * @param currentState
 * @returns
 */
export function useLocalState<S>(
  initialState: S | (() => S),
): [S, React.Dispatch<React.SetStateAction<S>>];
export function useLocalState<S = undefined>(): [
  S | undefined,
  React.Dispatch<React.SetStateAction<S | undefined>>,
];
export function useLocalState<T>(currentState?: T | (() => T)) {
  const currentResolvedState =
    typeof currentState === 'function'
      ? (currentState as () => T)()
      : currentState;
  const [localState, setLocalState] = useState(currentState);
  const [prevOutsideState, setPrevOutsideState] = useState(currentState);

  // if outside state has changed deeply, update local state.
  if (!isEqual(prevOutsideState, currentResolvedState)) {
    setLocalState(currentResolvedState);
    setPrevOutsideState(currentResolvedState);
  }

  // we return the local state and the setLocalState function.
  return [localState, setLocalState] as const;
}
